import { LocalDate } from '@js-joda/core';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { Country } from '@dreryk/address-common-api';
import { IdentityDocument } from '@dreryk/egabinet-patient-published-language-api';

import { FormContextValues, Translate } from '@dreryk/react-common';

import { FormSectionHeader } from '../common/FormSectionHeader';
import { IdentityDocumentIssuingCountryField } from '../common/IdentityDocument/IdentityDocumentIssuingCountryField';
import { IdentityDocumentSeriesAndNumberField } from '../common/IdentityDocument/IdentityDocumentSeriesAndNumberField';
import { IdentityDocumentValidToField } from '../common/IdentityDocument/IdentityDocumentValidToField';
import { identityDocumentTranslationKeys } from '../translations';

import {
   createIdentityDocumentSelectTypeVisitor,
   getIdentityDocumentSelectType,
   getIdentityDocumentType,
   IdentityDocumentSelectType,
   IdentityDocumentSelectTypeVisitor,
   IdentityDocumentTypeField,
   OnIdentityDocumentTypeSelected,
} from './IdentityDocumentTypeField';

export type IdentityDocumentFormPart = {
   type: IdentityDocumentSelectType;
   seriesAndNumber?: string | null;
   issuingCountry?: Country | null;
   validTo?: LocalDate | null;
};

export const createIdentityDocumentFormPart = (
   identityDocument: IdentityDocument | undefined
): IdentityDocumentFormPart => {
   if (!identityDocument) {
      return {
         type: 'None',
      };
   }

   return {
      type: getIdentityDocumentSelectType(identityDocument.getType()),
      seriesAndNumber: identityDocument.getSeriesAndNumber(),
      issuingCountry: identityDocument.getIssuingCountry(),
      validTo: identityDocument.getValidTo(),
   };
};

export const createIdentityDocumentFromFormPart = (
   identityDocumentFormPart: IdentityDocumentFormPart
): IdentityDocument | undefined => {
   if (identityDocumentFormPart.type === 'None') {
      return undefined;
   }

   return IdentityDocument.of(
      getIdentityDocumentType(identityDocumentFormPart.type)!,
      identityDocumentFormPart.seriesAndNumber!,
      identityDocumentFormPart.issuingCountry!,
      identityDocumentFormPart.validTo!
   );
};

const StyledIdentityDocumentFormContentSection = styled.div`
   display: grid;
   grid-template-columns: repeat(2, 1fr);
   gap: 24px;
   width: 640px;
`;

StyledIdentityDocumentFormContentSection.displayName = 'StyledIdentityDocumentFormSection';

type IdentityDocumentFormSectionProps = Pick<FormContextValues, 'register' | 'watch' | 'setValue'> & {
   readonly name: string;
};

export const IdentityDocumentFormSection = (props: IdentityDocumentFormSectionProps): JSX.Element => {
   const { name, setValue, register, watch } = props;

   const { type } = watch(name) as IdentityDocumentFormPart;

   const identityDocumentSelectTypeVisitor = useMemo<IdentityDocumentSelectTypeVisitor>(
      () => createIdentityDocumentSelectTypeVisitor(type),
      [type]
   );

   const handleIdentityDocumentTypeSelected = useCallback<OnIdentityDocumentTypeSelected>(() => {
      setValue(`${name}.seriesAndNumber`, null);
      setValue(`${name}.issuingCountry`, null);
      setValue(`${name}.validTo`, null);
   }, [name, setValue]);

   return (
      <div>
         <FormSectionHeader>
            <Translate>{identityDocumentTranslationKeys.identityDocument}</Translate>
         </FormSectionHeader>
         <StyledIdentityDocumentFormContentSection>
            <IdentityDocumentTypeField
               name={`${name}.type`}
               register={register}
               onSelect={handleIdentityDocumentTypeSelected}
            />
            {identityDocumentSelectTypeVisitor((currentType) => {
               if (currentType === 'None') {
                  return null;
               }

               return (
                  <>
                     <IdentityDocumentSeriesAndNumberField name={`${name}.seriesAndNumber`} register={register} />
                     <IdentityDocumentIssuingCountryField name={`${name}.issuingCountry`} register={register} />
                     <IdentityDocumentValidToField name={`${name}.validTo`} register={register} />
                  </>
               );
            })}
         </StyledIdentityDocumentFormContentSection>
      </div>
   );
};
